import dayjs from "dayjs"
import React, { useEffect } from "react"

import DatePicker from "../../components/shared/DatePicker"
import { Label, Select, TimePicker } from "../../components/shared/Inputs"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { useBookableEventTypes } from "../BookableEventTypes/BookableEventTypesContext"

import { useBookableEvents } from "./BookableEventsContext"

const BookableEventFormDetails = () => {
  const { bookableEvent, setBookableEvent, fetching } = useBookableEvents()
  const { bookableEventTypes, fetching: fetchingEventTypes } = useBookableEventTypes()

  // When passing a bookable event type id in the url, set the bookable event type id
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const bookableEventTypeId = urlParams.get("bookableEventTypeId")
    if (bookableEventTypeId) {
      setBookableEvent((prev) => ({ ...prev, bookableEventTypeId }))
      urlParams.delete("bookableEventTypeId")
      window.history.replaceState({}, "", window.location.pathname)
    }
  }, [])

  // Calculate end date for multi-day events
  const calculateEndDate = (startDate, durationDays) =>
    dayjs(startDate)
      .add(durationDays - 1, "day")
      .format("MM/DD/YYYY")

  // Handle date selection
  const handleDateChange = (date) => {
    setBookableEvent((event) => {
      // Preserve the time component when changing date
      const newStartsAt = dayjs(date)
        .hour(dayjs(event.startsAt).tz(event.timeZone).hour())
        .minute(dayjs(event.startsAt).tz(event.timeZone).minute())
        .toDate()

      let newEndsAt
      if (isMultiDay && selectedEventType?.duration) {
        // For multi-day events, adjust end date based on start date + duration
        newEndsAt = dayjs(newStartsAt)
          .add(selectedEventType.duration - 1, "day")
          .hour(dayjs(event.endsAt).tz(event.timeZone).hour())
          .minute(dayjs(event.endsAt).tz(event.timeZone).minute())
          .toDate()
      } else {
        // For single-day events, keep the same duration
        const durationMinutes = dayjs(event.endsAt).diff(dayjs(event.startsAt), "minute")
        newEndsAt = dayjs(newStartsAt).add(durationMinutes, "minute").toDate()
      }

      return { ...event, startsAt: newStartsAt, endsAt: newEndsAt }
    })
  }

  // Handle time selection
  const handleTimeChange = (timeString, isEndTime = false) => {
    const [hour, minute] = timeString.split(":")

    if (isEndTime) {
      setBookableEvent((event) => ({
        ...event,
        endsAt: dayjs(event.endsAt).set("hour", hour).set("minute", minute).toDate()
      }))
    } else {
      setBookableEvent((event) => {
        const newStartsAt = dayjs(event.startsAt).set("hour", hour).set("minute", minute).toDate()
        let newEndsAt = event.endsAt

        if (!isMultiDay) {
          // For single-day events, maintain the same duration
          const durationMinutes =
            selectedEventType?.durationUnit === "hour"
              ? (selectedEventType?.duration || 1) * 60
              : selectedEventType?.duration || 60

          newEndsAt = dayjs(newStartsAt).add(durationMinutes, "minute").toDate()
        }

        return { ...event, startsAt: newStartsAt, endsAt: newEndsAt }
      })
    }
  }

  // Handle timezone change
  const handleTimezoneChange = (newTimezone) => {
    setBookableEvent((event) => {
      const startsAt = dayjs(event.startsAt)
        .tz(newTimezone)
        .set("hour", dayjs(event.startsAt).tz(event.timeZone).get("hour"))
        .set("minute", dayjs(event.startsAt).tz(event.timeZone).get("minute"))
        .toISOString()

      const endsAt = dayjs(event.endsAt)
        .tz(newTimezone)
        .set("hour", dayjs(event.endsAt).tz(event.timeZone).get("hour"))
        .set("minute", dayjs(event.endsAt).tz(event.timeZone).get("minute"))
        .toISOString()

      return {
        ...event,
        timeZone: newTimezone,
        startsAt,
        endsAt
      }
    })
  }

  const selectedEventType = bookableEventTypes?.find(
    (et) => et.id.toString() === bookableEvent.bookableEventTypeId?.toString()
  )
  const isLoading = fetching || fetchingEventTypes
  const isMultiDay = selectedEventType?.durationUnit === "day"

  // Calculate end date for display in UI (for multi-day events)
  const endDate =
    isMultiDay && bookableEvent.startsAt && selectedEventType?.duration
      ? calculateEndDate(bookableEvent.startsAt, selectedEventType.duration)
      : null

  if (isLoading) {
    return <LoadingSpinner className="py-12" spinnerClassName="text-teal h-12 w-12" />
  }

  return (
    <>
      {selectedEventType && bookableEvent.timeZone && (
        <>
          {/* Date and Time Section */}
          {isMultiDay ? (
            // Multi-day layout
            <>
              <div className="mb-4 w-full">
                <Label>Start date</Label>
                <div className="flex items-center">
                  <DatePicker
                    className="w-full"
                    selected={new Date(dayjs(bookableEvent.startsAt).tz(bookableEvent.timeZone))}
                    onChange={handleDateChange}
                  />
                  {endDate && <div className="ml-4">Ends on {endDate}</div>}
                </div>
              </div>

              <div className="flex flex-row justify-between gap-4">
                <div className="w-full md:w-1/2">
                  <Label htmlFor="bookable-event-start-time">Start time</Label>
                  <TimePicker
                    id="bookable-event-start-time"
                    value={dayjs(bookableEvent.startsAt).tz(bookableEvent.timeZone).format("HH:mm")}
                    valueFormat="HH:mm"
                    showFifteenIncrements={true}
                    onChange={(e) => handleTimeChange(e.target.value)}
                  />
                  <div className="mt-1 text-sm text-gray-dark">Start time for first day.</div>
                </div>

                <div className="w-full md:w-1/2">
                  <Label htmlFor="bookable-event-end-time">End time</Label>
                  <TimePicker
                    id="bookable-event-end-time"
                    value={dayjs(bookableEvent.endsAt).tz(bookableEvent.timeZone).format("HH:mm")}
                    valueFormat="HH:mm"
                    showFifteenIncrements={true}
                    onChange={(e) => handleTimeChange(e.target.value, true)}
                  />
                  <div className="mt-1 text-sm text-gray-dark">End time for last day.</div>
                </div>
              </div>
            </>
          ) : (
            // Single-day layout - Date and Time side by side
            <div className="mb-4 flex flex-row gap-4 md:flex-col">
              <div className="w-full min_md:w-1/2">
                <Label>Start date</Label>
                <DatePicker
                  className="w-full"
                  selected={new Date(dayjs(bookableEvent.startsAt).tz(bookableEvent.timeZone))}
                  onChange={handleDateChange}
                />
              </div>
              <div className="w-full min_md:w-1/2">
                <Label htmlFor="bookable-event-start-time">Start time</Label>
                <TimePicker
                  id="bookable-event-start-time"
                  value={dayjs(bookableEvent.startsAt).tz(bookableEvent.timeZone).format("HH:mm")}
                  valueFormat="HH:mm"
                  showFifteenIncrements={true}
                  onChange={(e) => handleTimeChange(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="mt-4">
            <Label htmlFor="time-zone">Time zone</Label>
            <Select
              value={bookableEvent.timeZone}
              onChange={(e) => handleTimezoneChange(e.target.value)}
              id="time-zone">
              {Intl.supportedValuesOf("timeZone").map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </div>

          <div className="mt-4">
            <Label>Recurring</Label>
            <Select value="does_not_repeat" disabled={true}>
              <option value="does_not_repeat">Does not repeat</option>
            </Select>
          </div>
        </>
      )}
    </>
  )
}

export default BookableEventFormDetails
